import React, { useState, useEffect } from 'react';
import { Dialog, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ABLocationAutocomplete from './ABLocationAutoComplete';
import CurrencyInput from 'react-currency-input-field';
import { TimeSelect } from "../components/TimeSelect";

const DURATION_OPTIONS = Array.from({ length: 16 }, (_, i) => {
    const hours = 0.5 + i * 0.5; // 0.5, 1.0, 1.5, ..., 8.0
    let label;
    if (hours === 0.5) label = "30 minutes";
    else if (Number.isInteger(hours)) label = `${hours} ${hours === 1 ? "hour" : "hours"}`;
    else label = `${hours} hours`;
    return { value: label, label };
});

const BLURB_CHAR_LIMIT = 40;

function makeBlurb(text, limit = BLURB_CHAR_LIMIT) {
    if (!text) return "";
    const trimmed = text.trim();

    // If short enough, return as-is (no ellipsis)
    if (trimmed.length <= limit) return trimmed;

    // Take a slice and try to cut at the last space to avoid mid-word chops
    let slice = trimmed.slice(0, limit);
    const lastSpace = slice.lastIndexOf(" ");
    if (lastSpace > 16) {           // avoid backing up to near-zero
        slice = slice.slice(0, lastSpace);
    }

    // Remove trailing punctuation to keep a clean "...":
    slice = slice.replace(/[.,;:!?-]+$/, "");

    return slice + "...";
}

const IDEA_TEMPLATES = [
    {
        title: "Coffee & Chat",
        duration: "1 hour",
        desc: "Let’s meet at a cozy café, grab a drink, and chat about life, work, and things you like. I'll be at the entrance."
    },
    {
        title: "Walk & Talk",
        duration: "1 hour",
        desc: "A relaxed walk in the park while we have positivity focused talks. I'll be at the south entrance."
    },
    {
        title: "Pickleball Match",
        duration: "2 hours",
        desc: "Casual game at public courts. I’ll bring paddles if needed, beginner friendly! Meet me at the front desk."
    },
    {
        title: "Photo Walk",
        duration: "1.5 hours",
        desc: "Stroll and shoot together around graphic muruals. I’ll share tips and we’ll capture some great shots. I'll be at the garage next to the address."
    },
    {
        title: "City Dinner",
        duration: "2.5 hours",
        desc: "Let's spend time together enjoying an exotic cuisine. I'll be at the front entrance."
    },
    {
        title: "Shopping Run",
        duration: "3 hours",
        desc: "Hit a couple stores at the mall together, I’ll help you hunt for gems and give style ideas. We'll meet in Zara."
    }
];



export default function ABActivityModal({
    open,
    onClose,
    onSubmit,
    initialData = {}
}) {
    const [title, setTitle] = useState('');
    const [desc, setDesc] = useState('');
    const [duration, setDuration] = useState('1.5 hours');
    const [price, setPrice] = useState('');
    const [extraNotes, setExtraNotes] = useState('');
    const [location, setLocation] = useState('');

    const [costOptions, setCostOptions] = useState({
        included: true,
        separate: false
    });

    const [locationSelectedViaGoogle, setLocationSelectedViaGoogle] = useState(false);
    const [errors, setErrors] = useState({});
    const [showIdeas, setShowIdeas] = useState(false);


    useEffect(() => {
        if (open) {
            if (initialData && initialData.id) {
                setTitle(initialData.title || '');
                setDesc(initialData.desc || '');
                setDuration(initialData.duration || '1.5 hours');
                setPrice(initialData.price || '');
                setExtraNotes(initialData.extraNotes || '');
                setLocation(initialData.location || '');
                const initCost = initialData.costOptions || {};
                setCostOptions({
                    included: initCost.included || initCost.noExtra || (!initCost.separate),
                    separate: !!initCost.separate
                });
                setLocationSelectedViaGoogle(!!initialData.location);
            } else {
                // Fresh modal for new activity
                setTitle('');
                setDesc('');
                setDuration('1.5 hours');
                setPrice('');
                setExtraNotes('');
                setLocation('');
                setCostOptions({ included: true, separate: false });
                setLocationSelectedViaGoogle(false);
            }
            setErrors({});
        }
    }, [open, initialData]);

    const handleRateChange = (value) => {
        const n = Number(value);
        if (Number.isFinite(n)) {
            const over = n > 1000000;
            const capped = over ? 1000000 : n;
            setPrice(capped ? String(capped) : "");
            setErrors(prev => ({ ...prev, price: over ? 'Price cannot exceed $1,000,000' : '' }));
        } else {
            setPrice(value || "");
            setErrors(prev => ({ ...prev, price: '' }));
        }
    }

    const handleSubmit = () => {
        const newErrors = {};

        if (!title.trim()) newErrors.title = 'Activity title required';
        if (!desc.trim()) newErrors.desc = 'Description required';
        const numericPrice = Number(price);
        if (!price || numericPrice < 15) newErrors.price = 'Price of at least $15 required';
        if (numericPrice > 1000000) newErrors.price = 'Price cannot exceed $1,000,000';
        if (!locationSelectedViaGoogle) newErrors.location = 'Please select a valid address from suggestions';

        if (costOptions.separate && !extraNotes.trim()) {
            newErrors.extraNotes = 'Please describe additional costs';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        onSubmit({
            id: initialData?.id || Math.random().toString(36).slice(2),
            title,
            price,
            duration,
            desc,
            extraNotes,
            costOptions,
            location,
            blurb: makeBlurb(desc, BLURB_CHAR_LIMIT)
        });
        onClose();
    };
    const toggleCost = (key) => {
        setCostOptions(prev => {
            if (prev[key]) return prev;
            const updated = {
                included: key === 'included',
                separate: key === 'separate'
            };
            if (key === 'included') {
                setExtraNotes('');
                setErrors(prev => ({ ...prev, extraNotes: '' }));
            }
            setErrors(prev => ({ ...prev, cost: '' }));
            return updated;
        });
    };


    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <div className="cl-modal">
                <div className="cl-modal-header">
                    <IconButton onClick={onClose} className="cl-modal-close">
                        <CloseIcon className="cl-modal-close-button" />
                    </IconButton>
                    <h2 className="cl-modal-title">{initialData?.id ? 'Edit Activity' : 'Add New Activity'}</h2>
                </div>

                <div className="cl-modal-body">
                    <div className="cl-modal-section">
                        <label className="cl-modal-label">Activity Title</label>
                        <input
                            value={title}
                            onChange={(e) => {
                                setTitle(e.target.value);
                                setErrors(prev => ({ ...prev, title: '' }));
                            }} maxLength={40}
                            placeholder="e.g., Coffee at local café"
                            className="cl-text-input"
                        />
                        {errors.title && <div className="cl-error-text">{errors.title}</div>}
                    </div>

                    <div className="cl-modal-section">
                        <label className="cl-modal-label">Description</label>
                        <div className="cl-textarea-wrapper">
                            <textarea
                                value={desc}
                                onChange={(e) => {
                                    setDesc(e.target.value);
                                    setErrors(prev => ({ ...prev, desc: '' }));
                                }}
                                maxLength={250}
                                placeholder="e.g., Enjoy a hot cup of coffee and great conversation. Meet me at the entrance."
                                className="cl-textarea cl-textarea-activity-description"
                                rows={3}
                            />
                            <div className="cl-desc-counter">{desc.length}/250</div>
                        </div>
                        <button
                            type="button"
                            className="cl-ideas-toggle"
                            aria-expanded={showIdeas ? "true" : "false"}
                            onClick={() => setShowIdeas(v => !v)}
                        >
                            {showIdeas ? "Hide ideas" : "Need ideas?"}
                        </button>

                        {showIdeas && (
                            <div className="cl-ideas-panel">
                                <div className="cl-ideas-chips">
                                    {IDEA_TEMPLATES.map((tpl) => (
                                        <button
                                            key={tpl.title}
                                            type="button"
                                            className="cl-idea-chip"
                                            onClick={() => {
                                                setTitle(tpl.title);
                                                setDesc(tpl.desc);
                                                setDuration(tpl.duration);
                                            }}
                                        >
                                            {tpl.title}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {errors.desc && <div className="cl-error-text">{errors.desc}</div>}
                    </div>

                    <div className="cl-modal-section">
                        <label className="cl-modal-label">Duration</label>
                        <TimeSelect
                            value={duration}
                            options={DURATION_OPTIONS}
                            onChange={(v) => setDuration(v)}
                            allowClear={false}
                        />
                    </div>

                    <div className="cl-modal-section">
                        <label className="cl-modal-label">Price (per booking)</label>
                        <CurrencyInput
                            name="rate"
                            className="cl-text-input cl-currency-input"
                            value={price}
                            placeholder="Booking Price"
                            allowDecimals={false}
                            allowNegativeValue={false}
                            max={1000000}
                            prefix="$"
                            onValueChange={handleRateChange}
                        />
                        {errors.price && <div className="cl-error-text">{errors.price}</div>}
                    </div>

                    <div className="cl-modal-section">
                        <label className="cl-modal-label">Extra Costs?</label>
                        <div className="cl-cost-options">
                            <label><input type="checkbox" name="cost-option" checked={costOptions.included} onChange={() => toggleCost('included')} /> Included in price (tickets, coffee, none)</label>
                            <label><input type="checkbox" name="cost-option" checked={costOptions.separate} onChange={() => toggleCost('separate')} /> Paid separately (describe below)</label>
                        </div>


                        {costOptions.separate && (
                            <>
                                <div className="cl-textarea-wrapper">
                                    <textarea
                                        value={extraNotes}
                                        onChange={(e) => {
                                            setExtraNotes(e.target.value);
                                            setErrors(prev => ({ ...prev, extraNotes: '' }));
                                        }}
                                        maxLength={100}
                                        className="cl-textarea cl-textarea-extra-costs"
                                        placeholder="ex: Each person pays for their own coffee"
                                    />
                                    <div className="cl-notes-counter">{extraNotes.length}/100</div>
                                </div>
                                {errors.extraNotes && <div className="cl-error-text">{errors.extraNotes}</div>}
                            </>
                        )}
                    </div>

                    <div className="cl-modal-section">
                        <label className="cl-modal-label">Address</label>
                        <ABLocationAutocomplete
                            value={location}
                            onSelect={(place) => {
                                setLocation(place.formattedAddress);
                                setLocationSelectedViaGoogle(true);
                                setErrors(prev => ({ ...prev, location: '' }));
                            }}
                            types={['address']}
                            placeholder="e.g. 123 Main St, Miami, FL"
                        />
                        <p className="cl-section-hint">For safety, we recommend public places.</p>
                        {errors.location && <div className="cl-error-text">{errors.location}</div>}
                    </div>



                    <div className="cl-center-button">
                        <button className="cl-btn-primary cl-btn-activity" onClick={handleSubmit}>
                            {initialData?.id ? 'Submit' : 'Add Activity'}
                        </button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}
